import { useEffect } from 'react'

import { chatApi } from '@/features/chat/api'
import { unreadTotal } from '@/features/chat/format'
import { useUiStore } from '@/lib/ui/ui-store'

/**
 * Keeps the shell chat dot in sync outside the Chat tab. The native app polls
 * the two lists every 15s; realtime notifications also update the dot while
 * the socket is connected. This is UI state only (D-042), not a data cache.
 */
export function useGlobalChatUnread(): void {
  const setChatUnread = useUiStore((state) => state.setChatUnread)

  useEffect(() => {
    let cancelled = false
    let inFlight = false

    async function refresh() {
      if (inFlight || document.visibilityState === 'hidden') return
      inFlight = true
      try {
        const [activityChats, privateChats] = await Promise.all([
          chatApi.listActivityChats(),
          chatApi.listPrivateConversations(),
        ])
        if (!cancelled) {
          setChatUnread(
            unreadTotal(activityChats.items, privateChats.items) > 0,
          )
        }
      } catch {
        // Keep the last known dot while the API is temporarily unavailable.
      } finally {
        inFlight = false
      }
    }

    void refresh()
    const timer = window.setInterval(() => void refresh(), 15_000)
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)

    return () => {
      cancelled = true
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
  }, [setChatUnread])
}
